import React, { useEffect, useState } from "react";
import { useAtom } from "jotai";
import { currentUserAtom } from "../jotai/atoms";
import { styled } from "../stitches.config";
import { BOT_NAME } from "../consts";
import { generatenewSpaceId } from "../utils/generatenewSpaceId";
import { Button } from "../components/Button";
import LogOutButton from "./LogOutButton";
import SproutMark from "../assets/icons/sproutmark.svg";
import Link from "next/link";
import { SpaceSession } from "../types";
import { spaceDisplayName } from "../utils/spaceDisplayName";
import { useSendAnalyticsEvent } from "../analytics/events";

const flash_api_key = "32b0e72304bb4d0c981de568c5a24888";

export default function BigSpaceList() {
  const { status, sessions } = useSessionHistory();
  const sendAnalyticsEvent = useSendAnalyticsEvent();

  function createSpace() {
    const newSpaceId = generatenewSpaceId();
    window.open("/space/" + newSpaceId, "_self");
  }

  let content = null;

  switch (status) {
    case "fetching": {
      content = <StatusMessage>Loading spaces...</StatusMessage>;
      break;
    }
    case "error": {
      content = (
        <StatusMessage>
          Unable to load spaces at the moment...
          <br />
          Please try again in a few minutes
        </StatusMessage>
      );
      break;
    }
    case "fetched": {
      sendAnalyticsEvent({ name: "spacesIndexLoaded" });

      content =
        sessions && sessions.length > 0 ? (
          <SpaceContainer>
            {sessions.map(({ spaceId, spaceName }) => (
              <SpaceItem key={spaceId} id={spaceId} name={spaceName} />
            ))}
          </SpaceContainer>
        ) : (
          <Empty>
            <EmptyArrow />
            {`You don't have any spaces yet.`}
            <br />
            Create one!
          </Empty>
        );
      break;
    }
  }

  return (
    <>
      <PageHeader>
        <HeaderItemWrapper>
          <Button
            color="primary"
            style={{
              boxShadow: "0 1px 1px 0 hsla(0, 77%, 14%, 0.25)",
              fontWeight: "600",
            }}
            onClick={createSpace}
          >
            Create space
          </Button>
        </HeaderItemWrapper>

        <Link href="/" passHref>
          <a>
            <SproutMark height={32}></SproutMark>
          </a>
        </Link>

        <HeaderItemWrapper
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <LogOutButton />
        </HeaderItemWrapper>
      </PageHeader>
      {content}
    </>
  );
}

export function useSessionHistory() {
  type Res = {
    status: "fetching" | "fetched" | "error";
    sessions?: SpaceSession[];
    error?: Error;
  };

  const [res, setRes] = useState<Res>({ status: "fetching" });
  const [currentUser] = useAtom(currentUserAtom);

  useEffect(() => {
    (async function fetchHistory() {
      const res = await fetch("/api/space/session-history", {
        method: "GET",
      });

      if (res.status !== 200) {
        const error = await res.text();
        setRes({ status: "error", error: new Error(error) });
        return;
      }

      const sessions = await res.json();
      setRes({ status: "fetched", sessions });
    })();
  }, [currentUser?.anonymous]);

  return res;
}

export function screenshotImgUrl({ id }: { id: string }) {
  const url = `https://sprout.place/space/${id}?name=${BOT_NAME}`;
  return `https://api.apiflash.com/v1/urltoimage?access_key=${flash_api_key}\
&url=${encodeURIComponent(url)}\
&width=${1200}\
&height=${1040}\
&ttl=${7 * 24 * 60 * 60}\
&thumbnail_width=${600}\
&wait_for=${encodeURIComponent(".space-loaded")}\
&css=${encodeURI(
    ".navigation-bar{display: none;} .minimap{display: none;} .cursor{display: none}"
  )}`;
}

function SpaceItem({ id, name }: { id: string; name?: string }) {
  const displayName = spaceDisplayName(id, name);

  return (
    <Link href={`/space/${id}`} passHref>
      <SpaceContainerItem as="a">
        <Label>{displayName}</Label>
        <Img src={screenshotImgUrl({ id })} alt={`screenshot of space`} />
      </SpaceContainerItem>
    </Link>
  );
}

const PageHeader = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "$3",
});

const HeaderItemWrapper = styled("div", {
  flex: "1 1 auto",
});

const SpaceContainer = styled("div", {
  display: "grid",
  gridTemplateColumns:
    "repeat(auto-fit, minmax(max(240px, min(100%, 480px)), 1fr))",
  margin: "$4 $4",
  gap: "$5",
  "@bp1024": {
    gridTemplateColumns:
      "repeat(auto-fit, minmax(min(240px, max(100%, 480px)), 1fr))",
    margin: "$2 $4",
  },
});

const SpaceContainerItem = styled("div", {
  height: 240,
  width: "100%",
  flexGrow: 1,
  border: "2px solid $transparentBrown40",
  borderRadius: "$2",
  overflow: "hidden",
  position: "relative",
  cursor: "pointer",
});
const Empty = styled("div", {
  fontWeight: "$medium",
  padding: "0 $5",
  lineHeight: "$5",
});

const Img = styled("img", {
  width: "100%",
  height: "auto",
});

const Label = styled("h1", {
  position: "absolute",
  top: "$1",
  left: "$1",
  backgroundColor: "$labelGrey",
  padding: "$2",
  borderRadius: "$1",
  fontWeight: "$medium",
});

const StatusMessage = styled("div", {
  textAlign: "center",
  marginTop: "200px",
});

const EmptyArrow = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="21"
    height="46"
    fill="none"
    viewBox="0 0 21 46"
    style={{
      display: "block",
      marginLeft: "25px",
      marginBottom: "10px",
      opacity: 0.6,
    }}
  >
    <path
      fill="#431F1E"
      d="M5 0l7.075 9.125L.635 10.69 5 0zm14.388 45.79c-2.102-1.626-3.903-4.062-5.45-6.906-1.551-2.853-2.88-6.176-4.01-9.638-2.264-6.922-3.77-14.491-4.695-20.166l1.974-.322c.918 5.63 2.404 13.084 4.621 19.867 1.109 3.391 2.393 6.592 3.868 9.304 1.48 2.72 3.12 4.89 4.916 6.28l-1.224 1.582z"
    ></path>
  </svg>
);
